import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface PersonalInformationSectionProps {
  personalAttributes: Record<string, boolean | null>;
  availableAttributes: string[];
  onSetAttributeValue: (attribute: string, value: boolean | null) => void;
  onAddAttribute: (attribute: string) => void;
  onDeleteAttribute: (attribute: string) => void;
  onStartEditAttribute: (attribute: string) => void;
  onSaveEditAttribute: () => void;
  editingAttribute: string | null;
  editAttributeValue: string;
  onEditAttributeValueChange: (value: string) => void;
  onCancelEditAttribute: () => void;
  newAttribute: string;
  onNewAttributeChange: (value: string) => void;
}

const PersonalInformationSection: React.FC<PersonalInformationSectionProps> = ({
  personalAttributes,
  availableAttributes,
  onSetAttributeValue,
  onAddAttribute,
  onDeleteAttribute,
  onStartEditAttribute,
  onSaveEditAttribute,
  editingAttribute,
  editAttributeValue,
  onEditAttributeValueChange,
  onCancelEditAttribute,
  newAttribute,
  onNewAttributeChange,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const getAttributeValue = (attribute: string): boolean | null => {
    if (attribute in personalAttributes) return personalAttributes[attribute];
    return null;
  };

  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
            mb: 0,
            textTransform: "uppercase"
          }}>
          {t("coachingNavigator.clientOverview.personalInformation")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {t("coachingNavigator.clientOverview.editAttributes")}
        </Button>
      </Box>
      <Box
        sx={{
          maxHeight: 140,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {availableAttributes.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic"
            }}>
            {t("coachingNavigator.clientOverview.noAttributesDefinedYet")}
          </Typography>
        ) : (
          availableAttributes.map((attribute) => {
            const value = getAttributeValue(attribute);
            return (
              <Box
                key={attribute}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="body2" sx={{
                  fontWeight: 500
                }}>
                  {attribute}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Button
                    size="small"
                    variant={value === true ? "contained" : "outlined"}
                    color={value === true ? "success" : "inherit"}
                    onClick={() => onSetAttributeValue(attribute, true)}
                    sx={{ minWidth: 50, height: 28 }}
                  >
                    <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {t("coachingNavigator.clientOverview.yes")}
                  </Button>
                  <Button
                    size="small"
                    variant={value === false ? "contained" : "outlined"}
                    color={value === false ? "error" : "inherit"}
                    onClick={() => onSetAttributeValue(attribute, false)}
                    sx={{ minWidth: 50, height: 28 }}
                  >
                    <CloseIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {t("coachingNavigator.clientOverview.no")}
                  </Button>
                  <Button
                    size="small"
                    variant={value === null ? "contained" : "outlined"}
                    color={value === null ? "warning" : "inherit"}
                    onClick={() => onSetAttributeValue(attribute, null)}
                    sx={{ minWidth: 64, height: 28 }}
                  >
                    <RemoveIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {t("coachingNavigator.clientOverview.clear")}
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth slotProps={{ paper: {
          sx: { bgcolor: "#fff" },
        } }}
      >
        <DialogTitle>
          {t("coachingNavigator.clientOverview.managePersonalAttributes")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1, mb: 2, mt: 1 }}>
            <TextField
              value={newAttribute}
              onChange={(e) => onNewAttributeChange(e.target.value)}
              placeholder={t(
                "coachingNavigator.clientOverview.addAttributePlaceholder",
              )}
              fullWidth
              size="small"
              onKeyPress={(e) =>
                e.key === "Enter" && onAddAttribute(newAttribute)
              }
            />
            <IconButton
              onClick={() => onAddAttribute(newAttribute)}
              disabled={
                !newAttribute.trim() ||
                availableAttributes.includes(newAttribute.trim())
              }
              color="primary"
            >
              <AddIcon />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 1
            }}>
            {t("coachingNavigator.clientOverview.availableAttributes")}
          </Typography>
          <Box
            sx={{
              maxHeight: 300,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {availableAttributes.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic"
                }}>
                {t("coachingNavigator.clientOverview.noAttributesDefinedYet")}
              </Typography>
            ) : (
              availableAttributes.map((attribute) => (
                <Box
                  key={attribute}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  {editingAttribute === attribute ? (
                    <>
                      <TextField
                        value={editAttributeValue}
                        onChange={(e) =>
                          onEditAttributeValueChange(e.target.value)
                        }
                        size="small"
                        fullWidth
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") onSaveEditAttribute();
                          if (e.key === "Escape") onCancelEditAttribute();
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={onSaveEditAttribute}
                        disabled={
                          !editAttributeValue.trim() ||
                          (editAttributeValue.trim() !== attribute &&
                            availableAttributes.includes(
                              editAttributeValue.trim(),
                            ))
                        }
                      >
                        <CheckIcon color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={onCancelEditAttribute}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {attribute}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onStartEditAttribute(attribute)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteAttribute(attribute)}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </>
                  )}
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">
            {t("coachingNavigator.clientOverview.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PersonalInformationSection;
